/**
 * planScreenRead.ts — the plan screen's reads as ONE answer, and the early
 * start the pre-plan loader gives them.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: checkout, the plan screen
 * (`PlanSelection.tsx`) and the pre-plan loader (`checkoutLoaderContent.ts`).
 *
 * WHAT THE PLAN SCREEN READS, in order: the clinic's plan options, then — when
 * there is an assessment — this assessment's clinician recommendation, which
 * either leaves the full catalogue (Route 1) or filters it (Route 2, never
 * falling open). {@link readPlanScreen} runs them and answers with what the
 * screen will show: the options, or which of its two errors. It moved here out
 * of `PlanSelection`'s load unchanged, so there is ONE copy of the rule.
 *
 * WHY IT CAN START BEFORE THE SCREEN MOUNTS (release review, 2026-09-25). On the
 * pre-plan lane CheckoutFlow keeps "Getting your order ready" up for a set
 * loading time after its restore lands on the plan chooser. While the reads
 * started only when `PlanSelection` mounted, that time was dead: measured in
 * headless Chrome, the options read went out ~2.3s after the restore had
 * landed, a slow one put the plan screen's "Fetching your plan options…"
 * skeleton straight after the card, and a failed one could not show its error
 * before ~3.35s (it would have shown at ~1.03s without the hold — REQUIREMENTS
 * A8, a loader never delays an error). So CheckoutFlow starts these reads the
 * moment its restore lands on the chooser ({@link startPlanScreenRead}), waits
 * for them as part of the same wait, lets its loader go AT ONCE when they fail,
 * and hands the handle to `PlanSelection`, which opens on the answer instead of
 * reading again.
 *
 * ⚠️ NEVER A SECOND SOURCE OF TRUTH. The early read is the SAME call with the
 * same grant, taken by the ONE mount of the plan screen that follows it and
 * only when it was made for that screen's own clinic, assessment and grant
 * ({@link earlyReadFor}). A retry, and any later visit to the plan screen (an
 * Edit plan round trip), reads afresh: the catalogue a patient chooses from is
 * never older than the screen they are on.
 */
import { getPatientAssessmentRecommendation } from '../api/clinic';
import { getPlanOptions } from '../api/prices';
import type { PlanOption } from '../api/types';

/** What the plan screen will show once its reads are in. */
export type PlanScreenRead =
  /** `recommendedKeys` null: Route 1, the full catalogue. Otherwise Route 2's usable keys. */
  | { kind: 'ready'; options: PlanOption[]; recommendedKeys: string[] | null }
  /** The options read failed — "we could not load your plan options". */
  | { kind: 'error' }
  /** The recommendation read failed, or its keys match no option — never the full list. */
  | { kind: 'recs_error' };

/**
 * The plan screen's reads, in its order. NEVER REJECTS: every failure is one of
 * the two error answers, so a read nobody is listening to any more (a restore
 * superseded mid-flight) can never surface as an unhandled rejection.
 */
export function readPlanScreen(clinicSlug: string, assessmentId?: string, checkoutToken?: string): Promise<PlanScreenRead> {
  return readInOrder(clinicSlug, assessmentId, checkoutToken).catch((): PlanScreenRead => ({ kind: 'error' }));
}

async function readInOrder(clinicSlug: string, assessmentId?: string, checkoutToken?: string): Promise<PlanScreenRead> {
  const guestOpts = checkoutToken && assessmentId ? { checkoutToken, assessmentId } : undefined;
  const response = await getPlanOptions(clinicSlug, guestOpts);
  // A body with no options list is the failed read it would have thrown as when
  // the screen picked its opening option from it.
  if (!Array.isArray(response?.options)) return { kind: 'error' };

  // No assessment (standalone chooser): full catalogue.
  //
  // With an assessment we ALWAYS read the recommendations, including as a
  // guest — but NOT because "the grant is identity". It is not: the grant
  // proves WHICH checkout, never WHO is holding it, and the same conflation
  // opened a claim hole on this journey once already. It works because the
  // guest lane sends that grant and clinic-service VERIFIES it is bound to this
  // assessment before answering (its `:id/guest-recommendations` route). If
  // that check is ever removed the read becomes authorised by a UUID we mail
  // out in plan-links, and the payload names the POM a clinician chose for this
  // person.
  if (!assessmentId) return { kind: 'ready', options: response.options, recommendedKeys: null };

  let read;
  try {
    read = await getPatientAssessmentRecommendation(assessmentId, clinicSlug, checkoutToken ? { checkoutToken } : undefined);
  } catch {
    return { kind: 'recs_error' };
  }
  const recommended = read.recommendation?.productKeys ?? null;
  // null recommendation → Route 1 full catalogue.
  if (!recommended) return { kind: 'ready', options: response.options, recommendedKeys: null };
  // Route 2: clinician set present — must filter; never fall open.
  const usable = response.options.some((o) => recommended.includes(o.productKey)) ? recommended : null;
  if (!usable) return { kind: 'recs_error' };
  return { kind: 'ready', options: response.options, recommendedKeys: usable };
}

/** Whether the plan screen, given this answer, shows an error rather than options. */
export function planScreenShowsError(read: PlanScreenRead): boolean {
  return read.kind !== 'ready' || read.options.length === 0;
}

/**
 * Reads CheckoutFlow started for the plan screen before it mounted (see the
 * header). `settled` is the answer once it has landed, readable synchronously,
 * so a screen mounting after it opens ON it — no skeleton frame between the
 * loader and the plan.
 */
export interface EarlyPlanScreenRead {
  readonly clinicSlug: string;
  readonly assessmentId: string;
  readonly checkoutToken: string | undefined;
  readonly result: Promise<PlanScreenRead>;
  readonly settled: PlanScreenRead | null;
}

/** Starts the plan screen's reads now (CheckoutFlow, when its restore lands on the chooser). */
export function startPlanScreenRead(clinicSlug: string, assessmentId: string, checkoutToken: string | undefined): EarlyPlanScreenRead {
  let settled: PlanScreenRead | null = null;
  const result = readPlanScreen(clinicSlug, assessmentId, checkoutToken).then((read) => {
    settled = read;
    return read;
  });
  return {
    clinicSlug,
    assessmentId,
    checkoutToken,
    result,
    get settled() {
      return settled;
    },
  };
}

/**
 * The early read, if it was made for exactly this screen — the same clinic,
 * assessment and grant — else null (the screen reads for itself).
 */
export function earlyReadFor(
  early: EarlyPlanScreenRead | null | undefined,
  clinicSlug: string,
  assessmentId: string | undefined,
  checkoutToken: string | undefined,
): EarlyPlanScreenRead | null {
  if (!early) return null;
  return early.clinicSlug === clinicSlug && early.assessmentId === assessmentId && early.checkoutToken === checkoutToken
    ? early
    : null;
}
